import React from "react";
import { graphql, HeadProps, PageProps } from "gatsby";
import { HomepageNewQuery } from "types/graphql-types";
import GatsbyHeadOpenGraph from "components/SEO/GatsbyHeadOpenGraph";
import { isPresent } from "src/utils/checks";
import type { Organization } from "schema-dts";
import { jsonLdScriptProps } from "react-schemaorg";
import organizationSchema from "../components/SEO/organization-json-ld-data";
import { PageContext } from "types/pageContext";
import { prefetchMetaForPromoApi } from "components/SEO/utils";
import SanityBuilderToComponents from "components/PageBuilder";
import ClientOnly from "components/ClientOnly";
import EmotionalHeroBanner from "components/EmotionalHeroBanner/EmotionalHeroBanner";

const MaybeLastSeenProducts = React.lazy(
  () => import("components/LastSeenProducts/MaybeLastSeenProducts")
);
const FindYourCruiseSearchBox = React.lazy(
  () => import("components/FindYourCruiseSearchBox/FindYourCruiseSearchBox")
);

type Query = HomepageNewQuery & { page: NonNullable<HomepageNewQuery["page"]> };

const HomepageTemplate: React.FC<React.PropsWithChildren<PageProps<Query>>> = ({
  data
}) => {
  const page = data.page;
  const allSections = page.sections?.filter(isPresent) || [];

  return (
    <React.Fragment>
      <EmotionalHeroBanner
        data={page.heroMedia}
        buttonsInverted={false}
      ></EmotionalHeroBanner>

      <React.Suspense>
        <FindYourCruiseSearchBox
          fycPageUrl={data.fycPagePath}
          firstLastDepartureCruiseDates={data.firstLastDepartureCruiseDates}
          backgroundColor="white"
          enableSticky
        />
      </React.Suspense>

      <ClientOnly>
        <React.Suspense>
          <MaybeLastSeenProducts />
        </React.Suspense>
      </ClientOnly>

      <React.Fragment>
        {allSections.length > 0 && (
          <SanityBuilderToComponents
            sections={allSections}
            initialBackgroundColor="white"
          />
        )}
      </React.Fragment>
    </React.Fragment>
  );
};

export const Head = (props: HeadProps<HomepageNewQuery, PageContext>) => {
  const { data, pageContext } = props;

  return (
    <GatsbyHeadOpenGraph
      path={props.location.pathname}
      alternateLinks={pageContext.alternateLinks}
      languageCode={pageContext.languageCode}
      openGraph={data.page?.metadata?.openGraph}
      excludeFromSitemap={false}
      jsonLd={[
        jsonLdScriptProps<Organization>({
          "@context": "https://schema.org",
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          ...organizationSchema
        })
      ]}
    >
      {prefetchMetaForPromoApi()}
    </GatsbyHeadOpenGraph>
  );
};

export const query = graphql`
  query HomepageNew($id: String!) {
    firstLastDepartureCruiseDates: firstLastDepartureCruiseDates {
      minYearMonth
      maxYearMonth
    }

    page: sanityHomepage(id: { eq: $id }) {
      metadata {
        openGraph {
          ...OpenGraphMetaTags
        }
      }

      heroMedia {
        ...HeroMedia
      }

      enableUrgentMessageSlide
      urgentMessageSlide {
        localized
      }

      sections {
        ...PageBuilderSections
        ... on SanitySharedSectionRef {
          ...PageBuilderSharedSectionRef
        }
      }
    }

    fycPagePath
  }
`;

export default HomepageTemplate;
